import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Briefcase, BarChart3, Target, Radio, Rocket, Sparkles,
  Layers, Loader2, ChevronRight, TrendingUp, ShieldAlert,
  LineChart, ListChecks, Coins, FileText, Zap, Printer,
  Compass, Cog, Users, Shield, ClipboardCheck, Activity, GitBranch,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/* ---------------- helpers ---------------- */
const RAG = { green: "bg-emerald-600", yellow: "bg-amber-500", red: "bg-rose-600", null: "bg-stone-300" };
const RAG_TEXT = { green: "text-emerald-700", yellow: "text-amber-700", red: "text-rose-700", null: "text-stone-400" };

function Pct({ pct, rag }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`h-1.5 flex-1 rounded-full bg-stone-100 overflow-hidden`}>
        <div className={`h-full ${RAG[rag] || RAG.null}`} style={{ width: `${pct ?? 0}%` }}/>
      </div>
      <div className={`text-[11px] font-black w-10 text-right ${RAG_TEXT[rag] || RAG_TEXT.null}`}>
        {pct == null ? "—" : `${Math.round(pct)}%`}
      </div>
    </div>
  );
}

function DimCard({ label, dim }) {
  return (
    <div className="bg-white rounded-xl border border-stone-200 p-3" data-testid={`b360-dim-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase font-black tracking-wider text-stone-500">{label}</div>
        {dim.insufficient_data && (
          <span title={dim.methodology} className="text-[9px] text-amber-700 font-black">Insufficient data</span>
        )}
      </div>
      <Pct pct={dim.pct} rag={dim.rag}/>
      <div className="text-[9px] text-stone-400 mt-1 leading-tight" title={dim.methodology}>How: {dim.methodology}</div>
    </div>
  );
}

/* ---------------- tabs ---------------- */
const TABS = [
  { id: "dashboard", label: "Dashboard", icon: BarChart3 },
  { id: "profile",   label: "Profile",   icon: Briefcase },
  { id: "okr",       label: "OKR",       icon: Target },
  { id: "kpi",       label: "KPI",       icon: LineChart },
  { id: "strategy",  label: "Strategy",  icon: Compass },
  { id: "ops",       label: "Operations", icon: Cog },
  { id: "people",    label: "People",    icon: Users },
  { id: "risk",      label: "Risk",      icon: Shield },
  { id: "readiness", label: "Readiness", icon: Radio },
  { id: "reviews",   label: "Reviews",   icon: ClipboardCheck },
  { id: "health",    label: "Health",    icon: Activity },
  { id: "decisions", label: "Decisions", icon: GitBranch },
  { id: "expansion", label: "Expansion", icon: Rocket },
  { id: "actions",   label: "Actions",   icon: ListChecks },
  { id: "capital",   label: "Capital",   icon: Coins },
  { id: "ai",        label: "AI",        icon: Sparkles },
  { id: "advisor",   label: "Advisor v2", icon: Sparkles },
  { id: "evidence",  label: "Evidence",  icon: FileText },
  { id: "report",    label: "Report",    icon: FileText },
  { id: "more",      label: "More",      icon: Layers },
];

/* ---------------- main ---------------- */
export default function Business360() {
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/b360/dashboard");
      setDash(r.data);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Please log in."); nav("/"); return; }
      toast.error("Couldn't load Business 360");
    } finally { setBusy(false); }
  }, [nav]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-stone-50 pb-20" data-testid="b360-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-3 py-2 flex items-center gap-2">
          <button onClick={() => nav(-1)} className="p-2 -ml-1 rounded-full hover:bg-stone-100" data-testid="b360-back">
            <ArrowLeft size={18}/>
          </button>
          <Briefcase size={16} className="text-indigo-600"/>
          <div>
            <div className="font-black text-stone-900 text-sm leading-none">Business 360</div>
            <div className="text-[10px] text-stone-500 font-bold">Plan · Measure · Improve · Expand</div>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-3 pb-2 flex items-center gap-1 overflow-x-auto">
          {TABS.map((t) => {
            const Ic = t.icon;
            return (
              <button key={t.id}
                      onClick={() => setTab(t.id)}
                      data-testid={`b360-tab-${t.id}`}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black uppercase whitespace-nowrap ${
                        tab === t.id ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"
                      }`}>
                <Ic size={11}/> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-3 space-y-3">
        {busy && !dash && (
          <div className="py-16 text-center text-stone-500">
            <Loader2 size={20} className="animate-spin inline-block mr-1.5"/> Loading…
          </div>
        )}
        {tab === "dashboard" && dash && <Dashboard dash={dash}/>}
        {tab === "profile"   && <ProfileEditor onSaved={load}/>}
        {tab === "okr"       && <OKR/>}
        {tab === "kpi"       && <KPIEngine/>}
        {tab === "strategy"  && <Strategy/>}
        {tab === "ops"       && <Operations/>}
        {tab === "people"    && <People/>}
        {tab === "risk"      && <RiskCenter/>}
        {tab === "reviews"   && <ReviewsTab/>}
        {tab === "health"    && <HealthHistoryTab/>}
        {tab === "decisions" && <DecisionsTab/>}
        {tab === "readiness" && dash && <Readiness dims={dash.readiness}/>}
        {tab === "expansion" && <Expansion/>}
        {tab === "actions"   && <ActionCenter/>}
        {tab === "capital"   && <CapitalPlanner/>}
        {tab === "ai"        && <AIInsights/>}
        {tab === "advisor"   && <AdvisorV2 setTab={setTab}/>}
        {tab === "evidence"  && <EvidenceVaultTab/>}
        {tab === "report"    && <ReportsTab/>}
        {tab === "more"      && <ComingSoon items={dash?.coming_soon || []}/>}
      </div>
    </div>
  );
}

/* ---------------- dashboard tab ---------------- */
function Dashboard({ dash }) {
  const [pulse, setPulse] = useState(null);
  useEffect(() => { api.get("/b360/pulse").then((r) => setPulse(r.data)).catch(() => {}); }, []);
  return (
    <>
      {pulse && <MasterPulse p={pulse}/>}
      <div className="rounded-2xl bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-700 text-white p-4">
        <div className="text-[11px] uppercase font-black tracking-wider opacity-80">Welcome to Business 360</div>
        <div className="font-black text-lg mt-0.5">Turn your data into decisions.</div>
        <div className="text-[11px] opacity-80">Track performance · Find opportunities · Plan your expansion.</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="b360-topline">
        <DimCard label="Financial"  dim={dash.readiness.financial}/>
        <DimCard label="Operations" dim={dash.readiness.operations}/>
        <DimCard label="People"     dim={dash.readiness.people}/>
        <DimCard label="Growth"     dim={dash.readiness.growth}/>
      </div>
      <div className="bg-white rounded-2xl border border-stone-200 p-3">
        <div className="text-[10px] uppercase font-black text-stone-500 mb-2">OKR Weekly Progress</div>
        {(dash.okr || []).length === 0 && (
          <div className="text-xs text-stone-400 italic">No objectives yet — create one under the OKR tab.</div>
        )}
        {dash.okr.map((o) => (
          <div key={o.objective_id} className="py-1.5 border-b border-stone-100 last:border-0">
            <div className="text-sm font-black text-stone-900 truncate">{o.title}</div>
            <Pct pct={o.pct} rag={o.rag}/>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-stone-200 p-3">
        <div className="text-[10px] uppercase font-black text-stone-500 mb-2">Expansion Readiness</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {dash.expansion.map((p) => (
            <div key={p.path} className="border border-stone-200 rounded-xl p-2" data-testid={`b360-exp-${p.path}`}>
              <div className="text-[11px] font-black text-stone-900 uppercase">{p.path.replace(/_/g, " ")}</div>
              <Pct pct={p.pct} rag={p.rag}/>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-stone-200 p-3">
        <div className="text-[10px] uppercase font-black text-stone-500 mb-2">Financial Overview</div>
        <div className="grid grid-cols-2 gap-2 text-[11px]">
          {Object.entries(dash.financial).filter(([k]) => k !== "computed").map(([k, v]) => (
            <div key={k} className="flex justify-between bg-stone-50 rounded-lg px-2 py-1.5">
              <span className="uppercase font-black text-stone-500">{k.replace(/_/g, " ")}</span>
              <span className="font-black text-stone-900">{v == null ? "—" : Number(v).toLocaleString("en-IN")}</span>
            </div>
          ))}
        </div>
        {Object.keys(dash.financial.computed || {}).length > 0 && (
          <div className="text-[10px] text-stone-500 mt-2 italic">
            Estimates (from your inputs): {Object.entries(dash.financial.computed).map(
              ([k, v]) => `${k.replace(/_/g, " ")} = ${v}%`,
            ).join(" · ")}
          </div>
        )}
      </div>
      <div className="text-[10px] text-stone-400 italic" data-testid="b360-disclaimer">{dash.disclaimer}</div>
    </>
  );
}

/* ---------------- profile tab ---------------- */
function ProfileEditor({ onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/b360/profile").then((r) => setForm(r.data?.overlay || {}));
  }, []);
  const submit = async () => {
    setSaving(true);
    try {
      const clean = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== "" && v != null) clean[k] = typeof v === "string" && /revenue|expenses|profit|ebitda|assets|liabilities|debt|cash|inventory|receivables|payables/.test(k) ? Number(v) : v;
      });
      await api.post("/b360/profile", clean);
      toast.success("Profile saved");
      onSaved?.();
    } catch { toast.error("Couldn't save"); }
    finally { setSaving(false); }
  };
  const F = (name, label, type = "text") => (
    <label className="block">
      <span className="text-[10px] uppercase font-black text-stone-500">{label}</span>
      <input type={type} value={form[name] ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))}
              className="w-full px-2 py-1.5 rounded-lg border border-stone-200 text-sm"
              data-testid={`b360-profile-${name}`}/>
    </label>
  );
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-3 space-y-2" data-testid="b360-profile-editor">
      <div className="text-[10px] uppercase font-black text-stone-500">Business Profile</div>
      <div className="grid grid-cols-2 gap-2">
        {F("business_type", "Type")}
        {F("industry", "Industry")}
        {F("year_started", "Year Started", "number")}
        {F("employees", "Employees", "number")}
        {F("branches", "Branches", "number")}
        {F("dealers", "Dealers", "number")}
        {F("distributors", "Distributors", "number")}
        {F("franchises", "Franchises", "number")}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {F("products_services", "Products / Services")}
        {F("target_market", "Target Market")}
        {F("customer_base", "Customer Base")}
        {F("gst", "GST")}
        {F("trademark", "Brand / Trademark")}
        {F("management_team", "Management Team")}
      </div>
      <div className="text-[10px] uppercase font-black text-stone-500 mt-2">Financials (yearly)</div>
      <div className="grid grid-cols-2 gap-2">
        {F("annual_revenue", "Revenue", "number")}
        {F("annual_expenses", "Expenses", "number")}
        {F("net_profit", "Net Profit", "number")}
        {F("ebitda", "EBITDA", "number")}
        {F("assets", "Assets", "number")}
        {F("liabilities", "Liabilities", "number")}
        {F("debt", "Debt", "number")}
        {F("cash_bank", "Cash / Bank", "number")}
        {F("inventory", "Inventory", "number")}
        {F("receivables", "Receivables", "number")}
        {F("payables", "Payables", "number")}
      </div>
      <button onClick={submit} disabled={saving}
              data-testid="b360-profile-save"
              className="w-full mt-2 py-2 rounded-full bg-indigo-600 text-white font-black text-sm disabled:opacity-50">
        {saving ? <Loader2 className="w-4 h-4 animate-spin inline mr-1"/> : null}
        Save profile
      </button>
    </div>
  );
}

/* ---------------- OKR tab ---------------- */
function OKR() {
  const [rows, setRows] = useState([]);
  const [title, setTitle] = useState("");
  const load = useCallback(async () => {
    const r = await api.get("/b360/okr/objectives");
    setRows(r.data?.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const addObj = async () => {
    if (!title.trim()) return;
    await api.post("/b360/okr/objectives", { title });
    setTitle(""); load();
  };
  const addKR = async (oid) => {
    const t = window.prompt("Key-result title:");
    if (!t) return;
    const tgt = Number(window.prompt("Target (number):") || 0);
    if (!(tgt > 0)) return;
    await api.post(`/b360/okr/objectives/${oid}/key-results`, { title: t, target: tgt });
    load();
  };
  const logProgress = async (krid) => {
    const a = Number(window.prompt("Actual to log:") || 0);
    const c = Number(window.prompt("Confidence 0-100:") || 50);
    await api.post(`/b360/okr/kr/${krid}/progress`, { actual: a, confidence: c });
    load();
  };
  return (
    <div className="space-y-2" data-testid="b360-okr">
      <div className="bg-white border border-stone-200 rounded-2xl p-3 flex gap-2">
        <input value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Double annual revenue"
                data-testid="b360-okr-input"
                className="flex-1 px-2 py-1.5 rounded-lg border border-stone-200 text-sm"/>
        <button onClick={addObj} data-testid="b360-okr-add"
                className="px-3 py-1.5 rounded-full bg-indigo-600 text-white font-black text-xs">Add Objective</button>
      </div>
      {rows.length === 0 && <div className="text-xs text-stone-400 italic p-3">No objectives yet.</div>}
      {rows.map((o) => (
        <div key={o.objective_id} className="bg-white border border-stone-200 rounded-2xl p-3">
          <div className="flex items-center justify-between">
            <div className="font-black text-sm text-stone-900">{o.title}</div>
            <div className={`text-[10px] font-black px-1.5 py-0.5 rounded-full text-white ${RAG[o.rag] || RAG.null}`}>
              {o.pct == null ? "—" : `${Math.round(o.pct)}%`}
            </div>
          </div>
          <Pct pct={o.pct} rag={o.rag}/>
          <div className="mt-2 space-y-1.5">
            {(o.key_results || []).map((kr) => (
              <div key={kr.kr_id} className="text-[11px] bg-stone-50 rounded-lg p-2">
                <div className="flex justify-between">
                  <span className="font-black text-stone-800 truncate">{kr.title}</span>
                  <button onClick={() => logProgress(kr.kr_id)}
                          className="text-indigo-600 font-black">Log</button>
                </div>
                <div className="text-[10px] text-stone-500">
                  {kr.actual ?? 0} / {kr.target} {kr.unit || ""}
                </div>
                <Pct pct={kr.pct} rag={kr.rag}/>
              </div>
            ))}
            <button onClick={() => addKR(o.objective_id)}
                    className="text-[11px] font-black text-indigo-600">+ Add Key Result</button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- readiness tab ---------------- */
function Readiness({ dims }) {
  return (
    <div className="space-y-2" data-testid="b360-readiness">
      <div className="text-[10px] text-stone-500 italic">
        Every dimension shows how it was computed. Missing data is highlighted honestly.
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {Object.entries(dims).map(([k, v]) => (
          <DimCard key={k} label={k[0].toUpperCase() + k.slice(1)} dim={v}/>
        ))}
      </div>
    </div>
  );
}

/* ---------------- expansion tab ---------------- */
function Expansion() {
  const [plan, setPlan] = useState([]);
  const load = useCallback(async () => {
    const r = await api.get("/b360/expansion/plan");
    setPlan(r.data?.summary || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (path, item, status) => {
    await api.post("/b360/expansion/item", { path, item, status });
    load();
  };
  return (
    <div className="space-y-2" data-testid="b360-expansion">
      {plan.map((p) => (
        <div key={p.path} className="bg-white border border-stone-200 rounded-2xl p-3">
          <div className="flex items-center justify-between mb-1">
            <div className="font-black uppercase text-stone-900 text-sm">{p.path.replace(/_/g, " ")}</div>
            <div className={`text-[10px] font-black px-1.5 py-0.5 rounded-full text-white ${RAG[p.rag] || RAG.null}`}>
              {p.done_count}/{p.total}
            </div>
          </div>
          <Pct pct={p.pct} rag={p.rag}/>
          <div className="mt-2 space-y-1">
            {p.items.map((it) => (
              <div key={it.item} className="flex items-center gap-2 text-[11px]"
                    data-testid={`b360-exp-item-${p.path}-${it.item.slice(0, 20)}`}>
                <select value={it.status || "not_started"}
                          onChange={(e) => setStatus(p.path, it.item, e.target.value)}
                          className="px-1 py-0.5 rounded border border-stone-200 text-[10px]">
                  <option value="not_started">Not started</option>
                  <option value="in_progress">In progress</option>
                  <option value="done">Done</option>
                  <option value="not_applicable">N/A</option>
                </select>
                <span className="text-stone-800 flex-1">{it.item}</span>
                <span className="text-[9px] uppercase text-stone-400">{it.category}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- AI insights tab ---------------- */
function AIInsights() {
  const [focus, setFocus] = useState("analyse_business");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/b360/ai/insights", { focus, context_hint: note });
      setText(r.data?.insights || "");
    } catch { toast.error("AI unavailable"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-3 space-y-2" data-testid="b360-ai">
      <div className="text-[10px] uppercase font-black text-stone-500">AI Business Advisor</div>
      <select value={focus} onChange={(e) => setFocus(e.target.value)}
                data-testid="b360-ai-focus"
                className="w-full px-2 py-1.5 rounded-lg border border-stone-200 text-sm">
        <option value="analyse_business">Analyse business</option>
        <option value="find_gaps">Find gaps</option>
        <option value="action_plan">Create action plan</option>
        <option value="explain_kpi">Explain KPI</option>
        <option value="analyse_expansion">Analyse expansion</option>
        <option value="prepare_review">Prepare review</option>
      </select>
      <input value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Optional: add context (e.g. 'considering franchise in 2027')"
              data-testid="b360-ai-note"
              className="w-full px-2 py-1.5 rounded-lg border border-stone-200 text-sm"/>
      <button onClick={run} disabled={busy}
              data-testid="b360-ai-run"
              className="w-full py-2 rounded-full bg-indigo-600 text-white font-black text-sm disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin inline mr-1"/> : <Sparkles className="w-4 h-4 inline mr-1"/>}
        Generate insights
      </button>
      {text && (
        <div className="text-xs text-stone-800 whitespace-pre-wrap bg-stone-50 rounded-lg p-2" data-testid="b360-ai-output">{text}</div>
      )}
      <div className="text-[10px] text-stone-400 italic">
        Advisory only. AI never commits, invests, files or signs on your behalf.
      </div>
    </div>
  );
}

/* ---------------- coming soon tab ---------------- */
function ComingSoon({ items }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="b360-coming-soon">
      {items.map((label) => (
        <div key={label} className="bg-white border border-stone-200 rounded-2xl p-3">
          <div className="text-sm font-black text-stone-900">{label}</div>
          <div className="text-[10px] uppercase font-black text-amber-700 mt-1">Coming soon</div>
        </div>
      ))}
    </div>
  );
}


/* ---------------- KPI Engine tab ---------------- */
function KPIEngine() {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [dept, setDept] = useState("");
  const [direction, setDirection] = useState("higher_is_better");
  const [selected, setSelected] = useState(null);
  const [trend, setTrend] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/b360/kpi");
    setRows(r.data?.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const addKpi = async () => {
    if (!name.trim() || !(Number(target) > 0)) { toast.error("Name + target required"); return; }
    await api.post("/b360/kpi", {
      name: name.trim(), target: Number(target),
      department: dept || null, direction,
    });
    setName(""); setTarget(""); setDept(""); load();
  };
  const logValue = async (kpi_id) => {
    const v = Number(window.prompt("Actual value:") || 0);
    if (!(v >= 0)) return;
    await api.post(`/b360/kpi/${kpi_id}/record`, { value: v });
    load();
    if (selected === kpi_id) openTrend(kpi_id);
  };
  const openTrend = async (kpi_id) => {
    setSelected(kpi_id);
    const r = await api.get(`/b360/kpi/${kpi_id}/trend`);
    setTrend(r.data);
  };

  return (
    <div className="space-y-2" data-testid="b360-kpi">
      <div className="bg-white border border-stone-200 rounded-2xl p-3 space-y-2">
        <div className="text-[10px] uppercase font-black text-stone-500">New KPI</div>
        <div className="grid grid-cols-2 gap-2">
          <input placeholder="Name (e.g. Revenue)" value={name} onChange={(e) => setName(e.target.value)}
                 data-testid="b360-kpi-name"
                 className="px-2 py-1.5 rounded-lg border border-stone-200 text-sm col-span-2"/>
          <input placeholder="Target" type="number" value={target} onChange={(e) => setTarget(e.target.value)}
                 data-testid="b360-kpi-target"
                 className="px-2 py-1.5 rounded-lg border border-stone-200 text-sm"/>
          <input placeholder="Department" value={dept} onChange={(e) => setDept(e.target.value)}
                 className="px-2 py-1.5 rounded-lg border border-stone-200 text-sm"/>
          <select value={direction} onChange={(e) => setDirection(e.target.value)}
                    className="px-2 py-1.5 rounded-lg border border-stone-200 text-sm col-span-2">
            <option value="higher_is_better">Higher is better (revenue/orders)</option>
            <option value="lower_is_better">Lower is better (CAC / churn)</option>
          </select>
        </div>
        <button onClick={addKpi} data-testid="b360-kpi-add"
                className="w-full py-1.5 rounded-full bg-indigo-600 text-white font-black text-xs">Add KPI</button>
      </div>

      {rows.length === 0 && <div className="text-xs text-stone-400 italic p-3">No KPIs yet.</div>}
      {rows.map((k) => (
        <div key={k.kpi_id} className="bg-white border border-stone-200 rounded-2xl p-3"
             data-testid={`b360-kpi-row-${k.kpi_id}`}>
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <div className="font-black text-sm text-stone-900 truncate">{k.name}</div>
              <div className="text-[10px] text-stone-500">
                {k.department ? `${k.department} · ` : ""}target {k.target} {k.unit || ""} · {k.direction.replace(/_/g, " ")}
              </div>
            </div>
            <div className={`text-[10px] font-black px-1.5 py-0.5 rounded-full text-white ${RAG[k.rag] || RAG.null}`}>
              {k.pct == null ? "—" : `${Math.round(k.pct)}%`}
            </div>
          </div>
          <Pct pct={k.pct} rag={k.rag}/>
          <div className="flex items-center gap-2 mt-1.5">
            <button onClick={() => logValue(k.kpi_id)}
                    className="text-[11px] font-black text-indigo-600">Log value</button>
            <button onClick={() => openTrend(k.kpi_id)}
                    className="text-[11px] font-black text-stone-600">View trend</button>
          </div>
          {selected === k.kpi_id && trend && (
            <div className="mt-2 bg-stone-50 rounded-lg p-2">
              <div className="text-[10px] uppercase font-black text-stone-500 mb-1">Trend</div>
              {trend.points.length === 0 && <div className="text-xs italic text-stone-400">No records logged yet.</div>}
              <Sparkline points={trend.points.map(p => p.value)}/>
              <div className="grid grid-cols-4 gap-1 text-[10px] mt-1">
                {trend.points.slice(-8).map((p, i) => (
                  <div key={i} className="bg-white rounded px-1 py-0.5">
                    {Number(p.value).toLocaleString("en-IN")}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Sparkline({ points }) {
  if (!points || points.length === 0) return null;
  const w = 260, h = 40;
  const max = Math.max(...points), min = Math.min(...points);
  const span = max - min || 1;
  const step = w / Math.max(points.length - 1, 1);
  const d = points.map((v, i) => `${i === 0 ? "M" : "L"} ${i * step},${h - ((v - min) / span) * h}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-10" data-testid="b360-kpi-spark">
      <path d={d} fill="none" stroke="#4f46e5" strokeWidth="2"/>
    </svg>
  );
}

/* ---------------- Action Center tab ---------------- */
function ActionCenter() {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    const r = await api.get("/b360/actions");
    setRows(r.data?.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const fromGap = async () => {
    const r = await api.post("/b360/actions/from-gap", {});
    toast.success(`Created ${r.data.count} action${r.data.count === 1 ? "" : "s"}`);
    load();
  };
  const setStatus = async (aid, status) => {
    await api.post(`/b360/actions/${aid}`, { status });
    load();
  };
  const addManual = async () => {
    const title = window.prompt("Action title:");
    if (!title) return;
    await api.post("/b360/actions", { title });
    load();
  };
  return (
    <div className="space-y-2" data-testid="b360-actions">
      <div className="flex gap-2">
        <button onClick={fromGap} data-testid="b360-actions-from-gap"
                className="flex-1 py-2 rounded-full bg-indigo-600 text-white font-black text-xs inline-flex items-center justify-center gap-1">
          <Zap size={12}/> Turn readiness gaps into actions
        </button>
        <button onClick={addManual}
                className="px-3 py-2 rounded-full bg-stone-100 text-stone-700 font-black text-xs">+ Manual</button>
      </div>
      {rows.length === 0 && <div className="text-xs text-stone-400 italic p-3">No actions yet.</div>}
      {rows.map((a) => (
        <div key={a.action_id} className="bg-white border border-stone-200 rounded-2xl p-3"
              data-testid={`b360-action-${a.action_id}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[9px] font-black uppercase text-stone-500 flex items-center gap-1">
                {a.source} {a.priority && <span className={`px-1 py-0.5 rounded-full text-white ${a.priority === "high" || a.priority === "critical" ? "bg-rose-600" : a.priority === "medium" ? "bg-amber-500" : "bg-stone-400"}`}>{a.priority}</span>}
              </div>
              <div className="text-sm font-black text-stone-900">{a.title}</div>
              {(a.owner || a.due_date) && (
                <div className="text-[10px] text-stone-500">
                  {a.owner ? `Owner ${a.owner}` : ""} {a.due_date ? `· Due ${a.due_date}` : ""}
                </div>
              )}
            </div>
            <select value={a.status || "open"}
                     onChange={(e) => setStatus(a.action_id, e.target.value)}
                     className="text-[10px] rounded border border-stone-200 px-1 py-0.5">
              <option value="open">Open</option>
              <option value="in_progress">In progress</option>
              <option value="blocked">Blocked</option>
              <option value="done">Done</option>
              <option value="dropped">Dropped</option>
            </select>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Capital Planner tab ---------------- */
function CapitalPlanner() {
  const [form, setForm] = useState({
    name: "Scenario A", capital_required: 10000000,
    existing_capital: 0, own_contribution: 2000000,
    debt_amount: 3000000, debt_interest_pct: 12, debt_tenure_years: 5,
    equity_investment: 4000000, equity_ownership_pct: 20,
    projected_revenue_y1: 5000000, projected_revenue_growth_pct: 40,
    projected_ebitda_pct: 18, projection_years: 3,
  });
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);

  const compute = async () => {
    setBusy(true);
    try {
      const r = await api.post("/b360/capital/compute", form);
      setOut(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Compute failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { compute(); /* eslint-disable-line */ }, []);
  const save = async () => {
    await api.post("/b360/capital/save", form);
    toast.success("Scenario saved");
  };

  const F = (name, label, type = "number") => (
    <label className="block">
      <span className="text-[10px] uppercase font-black text-stone-500">{label}</span>
      <input type={type} value={form[name] ?? ""}
              onChange={(e) => setForm((f) => ({
                ...f, [name]: type === "number" ? Number(e.target.value) : e.target.value,
              }))}
              className="w-full px-2 py-1.5 rounded-lg border border-stone-200 text-sm"
              data-testid={`b360-cap-${name}`}/>
    </label>
  );

  return (
    <div className="space-y-2" data-testid="b360-capital">
      <div className="bg-white border border-stone-200 rounded-2xl p-3 space-y-2">
        <div className="text-[10px] uppercase font-black text-stone-500">Scenario inputs</div>
        <div className="grid grid-cols-2 gap-2">
          {F("name", "Name", "text")}
          {F("capital_required", "Capital required")}
          {F("existing_capital", "Existing capital")}
          {F("own_contribution", "Own contribution")}
          {F("debt_amount", "Debt amount")}
          {F("debt_interest_pct", "Debt rate %")}
          {F("debt_tenure_years", "Debt tenure (yrs)")}
          {F("equity_investment", "Equity investment")}
          {F("equity_ownership_pct", "Equity ownership %")}
          {F("projected_revenue_y1", "Y1 revenue")}
          {F("projected_revenue_growth_pct", "Revenue growth %")}
          {F("projected_ebitda_pct", "EBITDA %")}
          {F("projection_years", "Projection years")}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={compute} disabled={busy}
                  data-testid="b360-cap-compute"
                  className="py-1.5 rounded-full bg-indigo-600 text-white font-black text-xs disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1"/> : null}
            Recompute
          </button>
          <button onClick={save}
                  data-testid="b360-cap-save"
                  className="py-1.5 rounded-full bg-stone-100 text-stone-800 font-black text-xs">Save scenario</button>
        </div>
      </div>
      {out && (
        <div className="bg-white border border-stone-200 rounded-2xl p-3 space-y-2" data-testid="b360-cap-output">
          <div className="grid grid-cols-3 gap-2 text-[11px]">
            <div className="bg-stone-50 rounded-lg p-2">
              <div className="uppercase font-black text-stone-500 text-[9px]">Founder %</div>
              <div className="font-black text-stone-900 text-lg">{out.dilution.founder_pct_est}%</div>
            </div>
            <div className="bg-stone-50 rounded-lg p-2">
              <div className="uppercase font-black text-stone-500 text-[9px]">Investor %</div>
              <div className="font-black text-stone-900 text-lg">{out.dilution.investor_pct_est}%</div>
            </div>
            <div className="bg-stone-50 rounded-lg p-2">
              <div className="uppercase font-black text-stone-500 text-[9px]">Funding gap</div>
              <div className={`font-black text-lg ${out.funding_gap > 0 ? "text-rose-600" : "text-emerald-700"}`}>
                {Number(out.funding_gap).toLocaleString("en-IN")}
              </div>
            </div>
          </div>
          <div className="text-[10px] uppercase font-black text-stone-500">Cash-flow projection</div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead className="text-[9px] uppercase text-stone-500">
                <tr>
                  <th className="text-left py-1">Yr</th>
                  <th className="text-right">Revenue</th>
                  <th className="text-right">EBITDA</th>
                  <th className="text-right">Debt svc</th>
                  <th className="text-right">Cash flow</th>
                </tr>
              </thead>
              <tbody>
                {out.projection.map((y) => (
                  <tr key={y.year} className="border-t border-stone-100">
                    <td className="py-1 font-black">Y{y.year}</td>
                    <td className="text-right">{Number(y.revenue_est).toLocaleString("en-IN")}</td>
                    <td className="text-right">{Number(y.ebitda_est).toLocaleString("en-IN")}</td>
                    <td className="text-right">{Number(y.debt_service_est).toLocaleString("en-IN")}</td>
                    <td className={`text-right font-black ${y.cash_flow_est < 0 ? "text-rose-600" : "text-emerald-700"}`}>
                      {Number(y.cash_flow_est).toLocaleString("en-IN")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-amber-800 italic font-black" data-testid="b360-cap-disclaimer">
            {out.disclaimer}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Reports tab ---------------- */
function ReportsTab() {
  const [md, setMd] = useState("");
  const [busy, setBusy] = useState(false);
  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.get("/b360/report?fmt=md", { responseType: "text",
                                                          transformResponse: (v) => v });
      setMd(typeof r.data === "string" ? r.data : JSON.stringify(r.data));
    } catch { toast.error("Report failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { generate(); /* eslint-disable-line */ }, []);
  const openPrint = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/b360/report?fmt=html`;
    const w = window.open(url, "_blank");
    // The browser can Ctrl+P → Save as PDF.
    if (!w) toast("Pop-up blocked — allow to open the printable report");
  };
  return (
    <div className="space-y-2" data-testid="b360-report">
      <div className="flex gap-2">
        <button onClick={generate} disabled={busy}
                data-testid="b360-report-refresh"
                className="flex-1 py-2 rounded-full bg-indigo-600 text-white font-black text-xs inline-flex items-center justify-center gap-1 disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <FileText size={12}/>}
          Regenerate report
        </button>
        <button onClick={openPrint}
                data-testid="b360-report-print"
                className="px-3 py-2 rounded-full bg-stone-900 text-white font-black text-xs inline-flex items-center gap-1">
          <Printer size={12}/> Print / Save as PDF
        </button>
      </div>
      <div className="bg-white border border-stone-200 rounded-2xl p-3">
        <pre className="text-[11px] whitespace-pre-wrap font-mono text-stone-800"
              data-testid="b360-report-body">{md}</pre>
      </div>
      <div className="text-[10px] text-stone-400 italic">
        The printable version opens in a new tab — use your browser's Print → Save as PDF.
      </div>
    </div>
  );
}



/* ---------------- Master Pulse ---------------- */
function MasterPulse({ p }) {
  const fmt = (v) => v == null ? "—" : (typeof v === "number" ? v.toLocaleString("en-IN") : v);
  const bh = p.business_health || {};
  const stats = [
    { label: "OKRs",    value: fmt(p.okr_count) },
    { label: "KPIs",    value: fmt(p.kpi_total) },
    { label: "KPI red", value: fmt(p.kpi_red), warn: (p.kpi_red || 0) > 0 },
    { label: "Actions", value: fmt(p.open_actions) },
    { label: "Risks",   value: fmt(p.open_risks), warn: (p.critical_risks || 0) > 0 },
    { label: "Strat gaps",  value: fmt(p.strategy_gaps) },
    { label: "Ops gaps",    value: fmt(p.operations_gaps) },
    { label: "People gaps", value: fmt(p.people_gaps) },
  ];
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-3" data-testid="b360-pulse">
      <div className="flex items-center gap-2 mb-2">
        <Radio size={12} className="text-emerald-600 animate-pulse"/>
        <div className="text-[10px] uppercase tracking-widest font-black text-stone-800">Master Pulse</div>
        <div className="ml-auto text-[9px] text-stone-400">Where is my business today?</div>
      </div>
      <div className="grid grid-cols-4 gap-1.5 mb-2">
        {stats.map((s) => (
          <div key={s.label} className="bg-stone-50 rounded-lg p-1.5">
            <div className="text-[9px] uppercase font-black text-stone-500">{s.label}</div>
            <div className={`font-black text-sm ${s.warn ? "text-rose-600" : "text-stone-900"}`}>{s.value}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
        {Object.entries(bh).map(([k, v]) => (
          <div key={k} className="bg-stone-50 rounded-lg p-1.5">
            <div className="text-[9px] uppercase font-black text-stone-500">{k}</div>
            <Pct pct={v} rag={v == null ? null : v >= 80 ? "green" : v >= 60 ? "yellow" : "red"}/>
          </div>
        ))}
      </div>
      {p.capital_gap != null && (
        <div className={`text-[11px] mt-2 ${p.capital_gap > 0 ? "text-rose-600" : "text-emerald-700"} font-black`}>
          Capital gap (latest scenario): {Number(p.capital_gap).toLocaleString("en-IN")}
        </div>
      )}
      <div className="text-[9px] text-stone-400 mt-1 italic">{p.disclaimer}</div>
    </div>
  );
}

/* ---------------- Strategy tab ---------------- */
function Strategy() {
  const [sub, setSub] = useState("swot");
  const [rows, setRows] = useState({ swot: [], pestle: [], competitors: [] });
  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([
      api.get("/b360/swot"), api.get("/b360/pestle"), api.get("/b360/competitors"),
    ]);
    setRows({ swot: a.data.items || [], pestle: b.data.items || [], competitors: c.data.items || [] });
  }, []);
  useEffect(() => { load(); }, [load]);

  const addSwot = async () => {
    const cat = window.prompt("Category (Strength / Weakness / Opportunity / Threat):");
    if (!cat) return;
    const title = window.prompt("Title:");
    if (!title) return;
    await api.post("/b360/swot", { category: cat, title, impact: "High" });
    load();
  };
  const addPestle = async () => {
    const cat = window.prompt("Category (Political/Economic/Social/Technological/Legal/Environmental):");
    if (!cat) return;
    const factor = window.prompt("Factor:");
    if (!factor) return;
    await api.post("/b360/pestle", { category: cat, factor, impact: "High" });
    load();
  };
  const addCompetitor = async () => {
    const name = window.prompt("Competitor name:");
    if (!name) return;
    await api.post("/b360/competitors", { competitor: name });
    load();
  };
  const promote = async (source_type, id) => {
    await api.post("/b360/strategy/promote-to-objective", { source_type, source_id: id });
    toast.success("Promoted to Objective");
  };

  return (
    <div className="space-y-2" data-testid="b360-strategy">
      <div className="flex gap-1">
        {["swot", "pestle", "competitors"].map((k) => (
          <button key={k} onClick={() => setSub(k)}
                  data-testid={`b360-strategy-${k}`}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-black uppercase ${
                    sub === k ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"
                  }`}>{k}</button>
        ))}
      </div>
      {sub === "swot" && (
        <>
          <button onClick={addSwot} data-testid="b360-swot-add"
                  className="w-full py-1.5 rounded-full bg-indigo-600 text-white font-black text-xs">
            + Add SWOT
          </button>
          {rows.swot.map((s) => (
            <div key={s.swot_id} className="bg-white border border-stone-200 rounded-xl p-2"
                  data-testid={`b360-swot-row-${s.swot_id}`}>
              <div className="text-[9px] uppercase font-black text-stone-500">{s.category} · {s.impact}</div>
              <div className="text-sm font-black text-stone-900">{s.title}</div>
              <button onClick={() => promote("SWOT", s.swot_id)}
                       data-testid={`b360-swot-promote-${s.swot_id}`}
                       className="mt-1 text-[10px] font-black text-indigo-600">Promote to Objective →</button>
            </div>
          ))}
        </>
      )}
      {sub === "pestle" && (
        <>
          <button onClick={addPestle}
                  className="w-full py-1.5 rounded-full bg-indigo-600 text-white font-black text-xs">
            + Add PESTLE
          </button>
          {rows.pestle.map((p) => (
            <div key={p.pestle_id} className="bg-white border border-stone-200 rounded-xl p-2">
              <div className="text-[9px] uppercase font-black text-stone-500">{p.category} · {p.impact}</div>
              <div className="text-sm font-black text-stone-900">{p.factor}</div>
              <button onClick={() => promote("PESTLE", p.pestle_id)}
                       className="mt-1 text-[10px] font-black text-indigo-600">Promote to Objective →</button>
            </div>
          ))}
        </>
      )}
      {sub === "competitors" && (
        <>
          <button onClick={addCompetitor}
                  className="w-full py-1.5 rounded-full bg-indigo-600 text-white font-black text-xs">
            + Add Competitor
          </button>
          {rows.competitors.map((c) => (
            <div key={c.competitor_id} className="bg-white border border-stone-200 rounded-xl p-2">
              <div className="text-sm font-black text-stone-900">{c.competitor}</div>
              <div className="text-[10px] text-stone-500">{c.market_position || "—"}</div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

/* ---------------- Operations tab ---------------- */
function Operations() {
  const [sops, setSops] = useState([]);
  const [procs, setProcs] = useState([]);
  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api.get("/b360/sops"), api.get("/b360/processes")]);
    setSops(a.data.items || []); setProcs(b.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const addSop = async () => {
    const name = window.prompt("SOP name:");
    if (!name) return;
    await api.post("/b360/sops", { name, status: "Draft" });
    load();
  };
  const addProc = async () => {
    const name = window.prompt("Process name:");
    if (!name) return;
    await api.post("/b360/processes", { name });
    load();
  };
  return (
    <div className="space-y-2" data-testid="b360-ops">
      <div className="grid grid-cols-2 gap-2">
        <button onClick={addSop} className="py-1.5 rounded-full bg-indigo-600 text-white font-black text-xs">+ SOP</button>
        <button onClick={addProc} className="py-1.5 rounded-full bg-stone-900 text-white font-black text-xs">+ Process</button>
      </div>
      <div className="text-[10px] uppercase font-black text-stone-500">SOPs ({sops.length})</div>
      {sops.map((s) => (
        <div key={s.sop_id} className="bg-white border border-stone-200 rounded-xl p-2">
          <div className="text-sm font-black text-stone-900">{s.name}</div>
          <div className="text-[10px] text-stone-500">{s.status} · {s.department || "—"} · {s.version}</div>
        </div>
      ))}
      <div className="text-[10px] uppercase font-black text-stone-500">Processes ({procs.length})</div>
      {procs.map((p) => (
        <div key={p.process_id} className="bg-white border border-stone-200 rounded-xl p-2">
          <div className="text-sm font-black text-stone-900">{p.name}</div>
          {(p.gaps || []).length > 0 && (
            <div className="text-[10px] text-rose-600 font-black">Gaps: {p.gaps.join(", ")}</div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ---------------- People tab ---------------- */
function People() {
  const [depts, setDepts] = useState([]);
  const [emps, setEmps] = useState([]);
  const [skills, setSkills] = useState([]);
  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([
      api.get("/b360/departments"), api.get("/b360/employees"), api.get("/b360/skills"),
    ]);
    setDepts(a.data.items || []); setEmps(b.data.items || []); setSkills(c.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const addDept = async () => {
    const n = window.prompt("Department name:"); if (!n) return;
    await api.post("/b360/departments", { name: n }); load();
  };
  const addEmp = async () => {
    const n = window.prompt("Employee name:"); if (!n) return;
    const d = window.prompt("Department (optional):") || null;
    await api.post("/b360/employees", { name: n, department: d }); load();
  };
  const addSkill = async () => {
    const emp = window.prompt("Employee:"); if (!emp) return;
    const sk = window.prompt("Skill:"); if (!sk) return;
    const cur = Number(window.prompt("Current level 0-5:") || 0);
    const req = Number(window.prompt("Required level 0-5:") || 0);
    await api.post("/b360/skills", { employee: emp, skill: sk,
                                        current_level: cur, required_level: req });
    load();
  };
  return (
    <div className="space-y-2" data-testid="b360-people">
      <div className="grid grid-cols-3 gap-2">
        <button onClick={addDept} className="py-1.5 rounded-full bg-indigo-600 text-white font-black text-xs">+ Dept</button>
        <button onClick={addEmp} className="py-1.5 rounded-full bg-stone-900 text-white font-black text-xs">+ Employee</button>
        <button onClick={addSkill} className="py-1.5 rounded-full bg-stone-100 text-stone-700 font-black text-xs">+ Skill</button>
      </div>
      <div className="text-[10px] uppercase font-black text-stone-500">Departments ({depts.length})</div>
      {depts.map((d) => (
        <div key={d.department_id} className="bg-white border border-stone-200 rounded-xl p-2">
          <div className="text-sm font-black text-stone-900">{d.name}</div>
          <div className="text-[10px] text-stone-500">{d.head ? `Head: ${d.head}` : "—"}</div>
        </div>
      ))}
      <div className="text-[10px] uppercase font-black text-stone-500">Employees ({emps.length})</div>
      {emps.map((e) => (
        <div key={e.employee_id} className="bg-white border border-stone-200 rounded-xl p-2">
          <div className="text-sm font-black text-stone-900">{e.name}</div>
          <div className="text-[10px] text-stone-500">{e.role || "—"} · {e.department || "—"}</div>
        </div>
      ))}
      <div className="text-[10px] uppercase font-black text-stone-500">Skill gaps ({skills.filter((s) => (s.skill_gap || 0) > 0).length})</div>
      {skills.filter((s) => (s.skill_gap || 0) > 0).map((s) => (
        <div key={s.skill_id} className="bg-white border border-stone-200 rounded-xl p-2">
          <div className="text-sm font-black text-stone-900">{s.employee} · {s.skill}</div>
          <div className="text-[10px] text-rose-600 font-black">Gap {s.skill_gap} ({s.current_level} → {s.required_level})</div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Risk tab ---------------- */
function RiskCenter() {
  const [risks, setRisks] = useState([]);
  const [controls, setControls] = useState([]);
  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api.get("/b360/risks"), api.get("/b360/controls")]);
    setRisks(a.data.items || []); setControls(b.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const addRisk = async () => {
    const t = window.prompt("Risk title:"); if (!t) return;
    const p = Number(window.prompt("Probability 1-5:") || 3);
    const i = Number(window.prompt("Impact 1-5:") || 3);
    await api.post("/b360/risks", { title: t, probability: p, impact: i });
    load();
  };
  const addControl = async () => {
    const n = window.prompt("Control name:"); if (!n) return;
    await api.post("/b360/controls", { name: n, frequency: "Monthly" });
    load();
  };
  const fromGap = async () => {
    const r = await api.post("/b360/risks/from-gap", {});
    toast.success(`Created ${r.data.count} risk action(s)`);
  };
  const commonGap = async () => {
    const r = await api.post("/b360/common/from-gap", {});
    toast.success(`Created ${r.data.count} action(s) from all gaps`);
  };
  const RISK_BADGE = { Low: "bg-emerald-600", Medium: "bg-amber-500",
                          High: "bg-orange-600", Critical: "bg-rose-700" };
  return (
    <div className="space-y-2" data-testid="b360-risk">
      <div className="grid grid-cols-2 gap-2">
        <button onClick={addRisk} data-testid="b360-risk-add" className="py-1.5 rounded-full bg-indigo-600 text-white font-black text-xs">+ Risk</button>
        <button onClick={addControl} className="py-1.5 rounded-full bg-stone-900 text-white font-black text-xs">+ Control</button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button onClick={fromGap} data-testid="b360-risk-from-gap"
                className="py-1.5 rounded-full bg-emerald-600 text-white font-black text-[11px]">
          <Zap size={11} className="inline mr-1"/> Risks → Actions
        </button>
        <button onClick={commonGap}
                className="py-1.5 rounded-full bg-emerald-700 text-white font-black text-[11px]">
          <Zap size={11} className="inline mr-1"/> All Gaps → Actions
        </button>
      </div>
      <div className="text-[10px] uppercase font-black text-stone-500">Risks ({risks.length})</div>
      {risks.map((r) => (
        <div key={r.risk_id} className="bg-white border border-stone-200 rounded-xl p-2 flex items-center gap-2"
             data-testid={`b360-risk-row-${r.risk_id}`}>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-black text-stone-900 truncate">{r.title}</div>
            <div className="text-[10px] text-stone-500">P{r.probability} · I{r.impact} · owner {r.owner || "—"}</div>
          </div>
          <span className={`text-[9px] font-black text-white uppercase px-1.5 py-0.5 rounded-full ${RISK_BADGE[r.risk_level] || "bg-stone-400"}`}>
            {r.risk_level} · {r.risk_score}
          </span>
        </div>
      ))}
      <div className="text-[10px] uppercase font-black text-stone-500">Controls ({controls.length})</div>
      {controls.map((c) => (
        <div key={c.control_id} className="bg-white border border-stone-200 rounded-xl p-2">
          <div className="text-sm font-black text-stone-900">{c.name}</div>
          <div className="text-[10px] text-stone-500">{c.status} · {c.frequency}</div>
        </div>
      ))}
    </div>
  );
}



/* ---------------- Reviews tab (Slice-4) ---------------- */
function ReviewsTab() {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    const r = await api.get("/b360/reviews");
    setRows(r.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const openReview = async (rid) => {
    const r = await api.get(`/b360/reviews/${rid}`);
    setActive(r.data.review); setItems(r.data.items || []);
  };
  const startReview = async () => {
    const cycle = window.prompt("Cycle (daily/weekly/monthly/quarterly):", "weekly");
    if (!cycle) return;
    const r = await api.post("/b360/reviews", { cycle });
    load(); openReview(r.data.review.review_id);
  };
  const addItem = async () => {
    if (!active) return;
    const linked_type = window.prompt("Linked type (OKR/KPI/Action/Risk/Process/SOP):", "KPI");
    if (!linked_type) return;
    const linked_id = window.prompt("Linked id:");
    if (!linked_id) return;
    const planned = Number(window.prompt("Planned value (blank = skip):") || 0) || null;
    const actual  = Number(window.prompt("Actual value (blank = skip):")  || 0) || null;
    const next_action = window.prompt("Next action (optional):") || null;
    await api.post(`/b360/reviews/${active.review_id}/items`,
                    { linked_type, linked_id, planned, actual, next_action });
    openReview(active.review_id);
  };
  const closeReview = async () => {
    if (!active) return;
    const r = await api.post(`/b360/reviews/${active.review_id}/close`, {});
    toast.success(`Closed. ${r.data.count || 0} action(s) emitted.`);
    setActive(null); setItems([]); load();
  };
  return (
    <div className="space-y-2" data-testid="b360-reviews">
      <button onClick={startReview} data-testid="b360-review-start"
              className="w-full py-2 rounded-full bg-indigo-600 text-white font-black text-xs">
        + New review
      </button>
      {!active && rows.map((r) => (
        <button key={r.review_id} onClick={() => openReview(r.review_id)}
                className="w-full text-left bg-white border border-stone-200 rounded-xl p-2">
          <div className="flex items-center justify-between">
            <div className="font-black text-sm text-stone-900 uppercase">{r.cycle}</div>
            <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full text-white ${
              r.status === "closed" ? "bg-stone-500" : "bg-emerald-600"}`}>{r.status}</span>
          </div>
          <div className="text-[10px] text-stone-500">{r.item_count} items · {new Date(r.created_at).toLocaleString()}</div>
        </button>
      ))}
      {active && (
        <div className="bg-white border border-stone-200 rounded-2xl p-3 space-y-2"
             data-testid="b360-review-detail">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase font-black text-stone-500">{active.cycle} review</div>
              <div className="font-black text-sm">{active.status}</div>
            </div>
            <button onClick={() => { setActive(null); setItems([]); }}
                    className="text-xs text-stone-500 font-black">Close</button>
          </div>
          {items.map((it) => (
            <div key={it.review_item_id} className="bg-stone-50 rounded-lg p-2 text-[11px]">
              <div className="font-black">{it.linked_type} · {it.linked_id}</div>
              <div className="text-[10px] text-stone-600">
                planned {it.planned ?? "—"} · actual {it.actual ?? "—"} · variance {it.variance ?? "—"}
              </div>
              {it.next_action && <div className="text-[10px] text-indigo-700">Next: {it.next_action}</div>}
            </div>
          ))}
          {active.status !== "closed" && (
            <div className="grid grid-cols-3 gap-2">
              <button onClick={addItem} data-testid="b360-review-add-item"
                       className="py-1.5 rounded-full bg-stone-900 text-white font-black text-xs">+ Add item</button>
              <button onClick={async () => {
                try {
                  const r = await api.get(`/b360/review-prep?cycle=${active.cycle}`);
                  const drops = r.data.items || [];
                  if (drops.length === 0) { toast.info("No KPI/KR drops detected."); return; }
                  let added = 0;
                  for (const d of drops) {
                    try {
                      await api.post(`/b360/reviews/${active.review_id}/items`, {
                        linked_type: d.linked_type,
                        linked_id:   d.linked_id,
                        planned:     d.planned,
                        actual:      d.actual,
                        delay_reason: d.suggested_reason,
                      });
                      added++;
                    } catch { /* skip */ }
                  }
                  toast.success(`Auto-filled ${added} of ${drops.length} drops`);
                  openReview(active.review_id);
                } catch (e) { toast.error("Auto-fill failed"); }
              }} data-testid="b360-review-autofill"
                       className="py-1.5 rounded-full bg-amber-600 text-white font-black text-xs">
                ⚡ Auto-fill from drops
              </button>
              <button onClick={closeReview} data-testid="b360-review-close"
                       className="py-1.5 rounded-full bg-emerald-600 text-white font-black text-xs">Close & emit</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- Health History tab (Slice-4) ---------------- */
function HealthHistoryTab() {
  const [points, setPoints] = useState([]);
  const [days, setDays] = useState(30);
  const load = useCallback(async () => {
    const r = await api.get(`/b360/pulse/history?days=${days}`);
    setPoints(r.data.points || []);
  }, [days]);
  useEffect(() => { load(); }, [load]);
  const snapshotNow = async () => {
    await api.post("/b360/pulse/snapshot", {});
    toast.success("Snapshot captured");
    load();
  };
  const dims = ["financial", "operations", "people", "market", "governance",
                  "strategy", "growth", "innovation", "risk"];
  const series = (name) => points.map(p => (p.health?.[name] ?? null)).filter(v => v != null);
  return (
    <div className="space-y-2" data-testid="b360-health">
      <div className="flex items-center gap-2">
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                  className="px-2 py-1 rounded-lg border border-stone-200 text-xs">
          <option value={7}>7 days</option>
          <option value={30}>30 days</option>
          <option value={90}>90 days</option>
        </select>
        <button onClick={snapshotNow} data-testid="b360-health-snapshot"
                className="ml-auto px-3 py-1 rounded-full bg-indigo-600 text-white font-black text-[11px]">Snapshot now</button>
      </div>
      {points.length === 0 && (
        <div className="text-xs text-stone-400 italic p-3">No snapshots yet — snapshots are captured daily at 03:15 UTC.</div>
      )}
      {points.length > 0 && (
        <>
          {dims.map((d) => {
            const s = series(d);
            if (s.length === 0) return null;
            return (
              <div key={d} className="bg-white border border-stone-200 rounded-xl p-2"
                   data-testid={`b360-health-${d}`}>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase font-black text-stone-500">{d}</div>
                  <div className="text-xs font-black">{Math.round(s[s.length - 1])}%</div>
                </div>
                <Sparkline points={s}/>
              </div>
            );
          })}
        </>
      )}
      <div className="text-[10px] text-stone-400 italic">Real snapshots only — no synthetic backfill.</div>
    </div>
  );
}

/* ---------------- Decisions tab (Slice-4) ---------------- */
function DecisionsTab() {
  const [rows, setRows] = useState([]);
  const [playbooks, setPlaybooks] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const load = useCallback(async () => {
    const r = await api.get("/b360/decisions");
    setRows(r.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/b360/decisions/playbooks").then((r) => setPlaybooks(r.data.items || [])).catch(() => {});
  }, []);
  const addDecision = async () => {
    const title = window.prompt("Decision title:");
    if (!title) return;
    const opts = (window.prompt("Options (comma separated):", "Option A, Option B") || "")
        .split(",").map((s) => ({ label: s.trim() })).filter((o) => o.label);
    if (opts.length < 2) { toast.error("Need at least 2 options"); return; }
    await api.post("/b360/decisions", { title, options: opts });
    load();
  };
  const loadPlaybook = async (pb) => {
    const title = window.prompt("Decision title:", pb.title);
    if (!title) return;
    try {
      const description = [
        `Context: ${pb.context}`,
        "",
        "Requirements:",
        ...pb.requirements.map((r) => `  • ${r}`),
        "",
        "Gaps to close:",
        ...pb.gaps.map((g) => `  • ${g}`),
      ].join("\n");
      await api.post("/b360/decisions", {
        title,
        description,
        options: pb.options,
      });
      toast.success(`Loaded playbook: ${pb.title}`);
      setPickerOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const choose = async (did, options) => {
    const label = window.prompt(`Choose option (${options.map((o) => o.label).join(" / ")}):`);
    if (!label) return;
    try {
      await api.post(`/b360/decisions/${did}/choose`, { option_label: label });
      toast.success("Decision recorded");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Choose failed"); }
  };
  return (
    <div className="space-y-2" data-testid="b360-decisions">
      <div className="grid grid-cols-2 gap-2">
        <button onClick={addDecision} data-testid="b360-decision-add"
                className="py-2 rounded-full bg-indigo-600 text-white font-black text-xs">+ New decision</button>
        <button onClick={() => setPickerOpen((v) => !v)} data-testid="b360-decision-playbooks"
                className="py-2 rounded-full bg-amber-600 text-white font-black text-xs">
          {pickerOpen ? "Close playbooks" : "📖 Load playbook"}
        </button>
      </div>
      {pickerOpen && (
        <div className="bg-white border border-stone-200 rounded-2xl p-3 space-y-1"
             data-testid="b360-playbook-picker">
          {playbooks.map((pb) => (
            <button key={pb.id} onClick={() => loadPlaybook(pb)}
                    data-testid={`b360-playbook-${pb.id}`}
                    className="w-full text-left p-2 rounded-lg hover:bg-stone-50 border border-stone-100">
              <div className="font-black text-[12px] text-stone-900">{pb.title}</div>
              <div className="text-[10px] text-stone-500">{pb.context}</div>
              <div className="text-[10px] text-indigo-700 mt-1">
                {pb.options.map((o) => o.label).join(" · ")}
              </div>
            </button>
          ))}
        </div>
      )}
      {rows.length === 0 && <div className="text-xs italic text-stone-400 p-3">No decisions yet.</div>}
      {rows.map((d) => (
        <div key={d.decision_id} className="bg-white border border-stone-200 rounded-2xl p-3"
             data-testid={`b360-decision-${d.decision_id}`}>
          <div className="flex items-center justify-between">
            <div className="font-black text-sm text-stone-900 truncate">{d.title}</div>
            <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full text-white ${
              d.status === "chosen" ? "bg-emerald-600" : "bg-amber-500"}`}>{d.status}</span>
          </div>
          {d.description && (
            <pre className="mt-1 text-[10px] text-stone-600 whitespace-pre-wrap font-sans">{d.description}</pre>
          )}
          <div className="text-[10px] text-stone-500 mt-1">
            {d.options.map((o) => o.label).join(" · ")}
          </div>
          {d.chosen_option && (
            <div className="text-[11px] text-emerald-700 font-black mt-1">Chosen: {d.chosen_option}</div>
          )}
          {d.status === "open" && (
            <button onClick={() => choose(d.decision_id, d.options)}
                     className="mt-1 text-[11px] font-black text-indigo-600">Choose option →</button>
          )}
        </div>
      ))}
      <div className="text-[10px] text-stone-400 italic">
        Decisions are user-only. AI never auto-commits a decision on your behalf.
      </div>
    </div>
  );
}


/* ---------------- Advisor v2 tab (structured claims + clickable refs) ---------------- */
function AdvisorV2({ setTab }) {
  const [conv, setConv] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [resolvedRef, setResolvedRef] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await api.get("/b360/ai/conversations");
      setConv(r.data.items || []);
    } catch { /* ignored */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  const ask = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      await api.post("/b360/ai/advise", { question: q.trim() });
      setQ("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "AI unavailable"); }
    finally { setBusy(false); }
  };
  const openRef = async (ref) => {
    try {
      const r = await api.get(`/b360/refs/resolve?ref=${encodeURIComponent(ref)}`);
      setResolvedRef(r.data);
      toast.success(`Opened ${r.data.kind}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Ref not found"); }
  };
  return (
    <div className="space-y-2" data-testid="b360-advisor">
      <div className="bg-white border border-stone-200 rounded-2xl p-3">
        <div className="text-[10px] uppercase font-black text-stone-500 mb-1">Ask the Business Advisor</div>
        <textarea rows={3} value={q} onChange={(e) => setQ(e.target.value)}
                  data-testid="b360-advisor-q"
                  placeholder="e.g. Should we open a new branch next quarter?"
                  className="w-full px-2 py-1.5 rounded-lg border border-stone-200 text-sm"/>
        <button onClick={ask} disabled={busy || !q.trim()} data-testid="b360-advisor-ask"
                className="mt-2 w-full py-2 rounded-full bg-indigo-600 text-white font-black text-xs disabled:opacity-50">
          {busy ? "Thinking…" : "Ask"}
        </button>
        <div className="text-[10px] italic text-stone-500 mt-1">
          AI must classify every claim as FACT · CALCULATION · ASSUMPTION · MISSING_DATA · SUGGESTION. It never commits money or legal decisions.
        </div>
      </div>
      {resolvedRef && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-3" data-testid="b360-ref-detail">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase font-black text-indigo-700">{resolvedRef.kind} record</div>
            <button onClick={() => setResolvedRef(null)} className="text-[10px] font-black text-stone-500">close</button>
          </div>
          <pre className="text-[10px] whitespace-pre-wrap font-mono mt-1">
            {JSON.stringify(resolvedRef.record, null, 2)}
          </pre>
          <button onClick={() => setTab(resolvedRef.tab)}
                  className="mt-1 text-[11px] font-black text-indigo-700">
            Jump to {resolvedRef.tab} tab →
          </button>
        </div>
      )}
      {conv.length === 0 && <div className="text-xs italic text-stone-400 p-3">No conversations yet.</div>}
      {conv.map((c) => (
        <div key={c.conv_id} className="bg-white border border-stone-200 rounded-2xl p-3"
             data-testid={`b360-advisor-conv-${c.conv_id}`}>
          <div className="text-[10px] uppercase font-black text-stone-500">
            {new Date(c.created_at).toLocaleString()}
          </div>
          <div className="font-black text-sm mt-0.5">{c.question}</div>
          <div className="mt-2 space-y-1">
            {(c.claims || []).map((cl, i) => {
              const kind = (cl.kind || "").toLowerCase();
              const badge = {
                fact:         "bg-emerald-600",
                calculation:  "bg-blue-600",
                assumption:   "bg-amber-500",
                missing_data: "bg-rose-600",
                suggestion:   "bg-indigo-600",
              }[kind] || "bg-stone-500";
              return (
                <div key={i} className="bg-stone-50 rounded-lg p-2">
                  <div className="flex items-center gap-1 mb-1">
                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full text-white uppercase ${badge}`}>
                      {kind || "—"}
                    </span>
                  </div>
                  <div className="text-[12px] text-stone-800">{cl.text || cl.claim || JSON.stringify(cl)}</div>
                  {Array.isArray(cl.refs) && cl.refs.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {cl.refs.map((r, j) => (
                        <button key={j} onClick={() => openRef(r)}
                                data-testid={`b360-ref-${r}`}
                                className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200 hover:bg-indigo-200">
                          {r}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}


/* ---------------- Evidence Vault tab (upload via Object Storage) ---------------- */
function EvidenceVaultTab() {
  const [items, setItems] = useState([]);
  const [entityType, setEntityType] = useState("decision");
  const [entityId, setEntityId] = useState("");
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await api.get("/b360/evidence");
      setItems(r.data.items || []);
    } catch { /* ignored */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  const upload = async () => {
    if (!file) { toast.error("Pick a file first"); return; }
    if (!entityId.trim()) { toast.error("Entity id required (e.g. decision_id, risk_id)"); return; }
    setBusy(true);
    try {
      // 1. Upload to Emergent Object Storage via multipart.
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", `b360-evidence:${entityType}:${entityId}`);
      const up = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const url = `${backendUrl}/api/files/${up.data.file_id}/download`;
      // 2. Register the evidence record.
      await api.post("/b360/evidence", {
        entity_type: entityType,
        entity_id: entityId.trim(),
        title: file.name,
        url,
        mime: file.type,
        size: file.size,
      });
      toast.success("Evidence uploaded");
      setFile(null);
      setEntityId("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="b360-evidence">
      <div className="bg-white border border-stone-200 rounded-2xl p-3">
        <div className="text-[10px] uppercase font-black text-stone-500 mb-1">Attach evidence</div>
        <div className="text-[11px] text-stone-500 mb-2">
          Any PDF or document, attached to a Decision, Risk, Review, or Action.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <label className="block">
            <span className="text-[9px] uppercase font-black text-stone-500">Attach to entity</span>
            <select value={entityType} onChange={(e) => setEntityType(e.target.value)}
                    data-testid="b360-evidence-entity-type"
                    className="w-full px-2 py-1.5 rounded-lg border border-stone-200 text-sm">
              <option value="decision">Decision</option>
              <option value="risk">Risk</option>
              <option value="review">Review</option>
              <option value="action">Action</option>
              <option value="okr">OKR</option>
              <option value="kpi">KPI</option>
            </select>
          </label>
          <label className="block">
            <span className="text-[9px] uppercase font-black text-stone-500">Entity id</span>
            <input value={entityId} onChange={(e) => setEntityId(e.target.value)}
                   data-testid="b360-evidence-entity-id"
                   placeholder="dec_… / risk_… / rev_… etc."
                   className="w-full px-2 py-1.5 rounded-lg border border-stone-200 text-sm"/>
          </label>
        </div>
        <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)}
               data-testid="b360-evidence-file"
               className="mt-2 w-full text-[11px]"/>
        <button onClick={upload} disabled={busy || !file || !entityId.trim()}
                data-testid="b360-evidence-upload"
                className="mt-2 w-full py-2 rounded-full bg-amber-600 text-white font-black text-xs disabled:opacity-50">
          {busy ? "Uploading…" : "Upload evidence"}
        </button>
      </div>
      {items.length === 0 && <div className="text-xs italic text-stone-400 p-3">No evidence yet.</div>}
      {items.map((e) => (
        <div key={e.evidence_id} className="bg-white border border-stone-200 rounded-xl p-2"
             data-testid={`b360-evidence-${e.evidence_id}`}>
          <div className="font-black text-[12px]">{e.title}</div>
          <div className="text-[10px] text-stone-500">{e.entity_type} · {e.entity_id} · {e.mime}</div>
          {e.url && (
            <a href={e.url} target="_blank" rel="noreferrer"
               className="text-[11px] font-black text-indigo-600">Open →</a>
          )}
        </div>
      ))}
    </div>
  );
}


